/**
 * API client for communicating with the backend
 */

import type {
  ImageWithCategories,
  ImageWithStats,
  ImageStats,
  GlobalStats,
  Contradiction,
} from '@doyoulikethis/shared';

/**
 * Get the API base URL based on the current environment.
 * In development (localhost), we use the Vite proxy at /api.
 * In production, we use the full API URL.
 */
function getApiBase(): string {
  // Check if we're on localhost (development)
  if (typeof window !== 'undefined' && window.location.hostname === 'localhost') {
    return '/api';
  }

  // Production: use the API subdomain
  return 'https://api.doyoulikethis.work/api';
}

const API_BASE = getApiBase();

// =============================================================================
// API Response Types
// =============================================================================

interface RandomImageResponse {
  success: boolean;
  image?: {
    id: string;
    title_en: string;
    title_ja: string | null;
    cdn_url: string;
    categories: Array<{
      id: string;
      display_name_en: string;
      display_name_ja: string | null;
    }>;
  };
  error?: string;
  message?: string;
}

interface VoteResponse {
  success: boolean;
  image?: ImageWithStats & { stats: ImageStats };
  contradiction?: Contradiction | null;
  error?: string;
  message?: string;
}

interface ImageStatsResponse {
  success: boolean;
  image_id?: string;
  stats?: ImageStats;
  error?: string;
  message?: string;
}

interface GlobalStatsResponse {
  success: boolean;
  stats?: GlobalStats;
  error?: string;
  message?: string;
}

// =============================================================================
// API Error
// =============================================================================

export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// =============================================================================
// Fetch Wrapper
// =============================================================================

async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE}${endpoint}`;

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new ApiError(
      data.error || 'unknown_error',
      data.message || 'An unexpected error occurred',
      response.status
    );
  }

  return data;
}

// =============================================================================
// Image API
// =============================================================================

/**
 * Get a random image for voting
 *
 * @param votedIds - Array of already-voted image IDs to exclude
 */
export async function getRandomImage(
  votedIds: string[] = []
): Promise<RandomImageResponse['image'] | null> {
  const params = votedIds.length > 0 ? `?voted=${votedIds.join(',')}` : '';

  try {
    const response = await apiFetch<RandomImageResponse>(
      `/image/random${params}`
    );
    return response.image ?? null;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'no_images_available') {
      return null;
    }
    throw error;
  }
}

/**
 * Get full image details by ID
 */
export async function getImage(
  id: string
): Promise<ImageWithCategories | null> {
  try {
    const response = await apiFetch<{ success: boolean; image: ImageWithCategories }>(
      `/image/${id}`
    );
    return response.image;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'not_found') {
      return null;
    }
    throw error;
  }
}

/**
 * Get statistics for an image
 */
export async function getImageStats(id: string): Promise<ImageStats | null> {
  try {
    const response = await apiFetch<ImageStatsResponse>(`/image/${id}/stats`);
    return response.stats ?? null;
  } catch (error) {
    if (error instanceof ApiError && error.code === 'not_found') {
      return null;
    }
    throw error;
  }
}

// =============================================================================
// Vote API
// =============================================================================

export interface SubmitVoteParams {
  imageId: string;
  liked: boolean;
  reasons: string[];
  fingerprint: string;
  timeToVoteMs: number;
  timeToReasonsMs: number;
}

export interface SubmitVoteResult {
  image: ImageWithStats & { stats: ImageStats };
  contradiction: Contradiction | null;
}

/**
 * Submit a vote for an image
 */
export async function submitVote(
  params: SubmitVoteParams
): Promise<SubmitVoteResult> {
  const response = await apiFetch<VoteResponse>('/vote', {
    method: 'POST',
    body: JSON.stringify({
      image_id: params.imageId,
      liked: params.liked,
      reasons: params.reasons,
      fingerprint: params.fingerprint,
      time_to_vote_ms: params.timeToVoteMs,
      time_to_reasons_ms: params.timeToReasonsMs,
    }),
  });

  if (!response.image) {
    throw new ApiError('invalid_response', 'Invalid response from server');
  }

  return {
    image: response.image,
    contradiction: response.contradiction ?? null,
  };
}

// =============================================================================
// Stats API
// =============================================================================

/**
 * Get global statistics
 */
export async function getGlobalStats(): Promise<GlobalStats | null> {
  try {
    const response = await apiFetch<GlobalStatsResponse>('/stats/global');
    return response.stats ?? null;
  } catch (error) {
    console.error('Failed to fetch global stats:', error);
    return null;
  }
}

// =============================================================================
// Suggestion API
// =============================================================================

export interface SubmitSuggestionParams {
  imageUrl: string;
  sourceContext?: string;
  submitterGuess?: 'ai' | 'human' | 'unsure';
  suggestedCategories?: string[];
  fingerprint: string;
}

interface SuggestResponse {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Submit an image suggestion
 */
export async function submitSuggestion(
  params: SubmitSuggestionParams
): Promise<{ message: string }> {
  const response = await apiFetch<SuggestResponse>('/suggest', {
    method: 'POST',
    body: JSON.stringify({
      image_url: params.imageUrl,
      source_context: params.sourceContext,
      submitter_guess: params.submitterGuess,
      suggested_categories: params.suggestedCategories,
      fingerprint: params.fingerprint,
    }),
  });

  return {
    message: response.message || 'Suggestion submitted successfully',
  };
}
